import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import {
  getFormatDateFromStoreDate,
  TextareaWithConfirm,
  RadioOptionsWithConfirm,
  SelectWithOtherWithConfirm,
  YearInputWithConfirm,
  CardContainer,
  CardWithModal
} from 'shared-components';

import QuestionPage from '../../../templates/question';
import Visas from './visas';
import * as types from 'types';
import { Q } from '../../../../store/student/types';
import { ConfigField } from '../../../../types';
import { IStudentVisa } from '../../../../types';
import { titleAppend } from '../../../../utilities/head';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import * as StudentActions from '../../../../store/student/actions';
import * as HistoryActions from '../../../../store/student/history/actions';
import {
  createNavigateForwardAction,
  createNavigateBackwardAction,
  createNavigateToAction
} from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/history/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../store/ui/actions';

const defaultValues = {
  lastCourse: '',
  lastCourseOther: '',
  lastCourseYear: '',
  hasOverseasTravel: undefined,
  overseasTravelTimes: '',
  hasUkTravel: undefined,
  ukTravelTimes: '',
  hasPreviousUkStudy: undefined,
  hasPreviousUkStudyVisa: undefined,
  previousUkStudyVisas: []
};

const useHistoryQPage1 = () => {
  const history = useSelector((state: ApplicationState) => state.student.history);
  const config = useSelector((state: ApplicationState) => state.config);
  const {
    LastCourse: lastCourseConfig,
    LastCourseYear: lastCourseYearConfig,
    HasOverseasTravel: hasOverseasTravelConfig,
    OverseasTravelTimes: overseasTravelTimesConfig,
    HasUKTravel: hasUkTravelConfig,
    UKTravelTimes: ukTravelTimesConfig,
    HasPreviousUkStudy: hasPreviousUkStudyConfig,
    HasPreviousUkStudyVisa: hasPreviousUkStudyVisaConfig,
    LastCourseLevel: lastCourseLevelConfig
  }: {
    [key: string]: ConfigField;
  } = useSelector((state: ApplicationState) => state.config.Checklist.History.Page1);

  const {
    lastCourse,
    lastCourseOther,
    lastCourseYear,
    hasOverseasTravel,
    overseasTravelTimes,
    hasUkTravel,
    ukTravelTimes,
    hasPreviousUkStudy,
    hasPreviousUkStudyVisa,
    previousUkStudyVisas,
    lastCourseLevel,
    lowerOrSameLevelReason
  } = { ...defaultValues, ...history };

  const { complete, visible } = getPageStatus(config, {
    lastCourse,
    lastCourseOther,
    lastCourseYear,
    hasOverseasTravel,
    overseasTravelTimes,
    hasUkTravel,
    ukTravelTimes,
    hasPreviousUkStudy,
    hasPreviousUkStudyVisa,
    previousUkStudyVisas,
    lastCourseLevel,
    lowerOrSameLevelReason
  });

  const {
    lastCourse: lastCourseVisible,
    lastCourseYear: lastCourseYearVisible,
    hasOverseasTravel: hasOverseasTravelVisible,
    overseasTravelTimes: overseasTravelTimesVisible,
    hasUkTravel: hasUkTravelVisible,
    ukTravelTimes: ukTravelTimesVisible,
    hasPreviousUkStudy: hasPreviousUkStudyVisible,
    lastCourseLevel: lastCourseLevelVisible,
    lowerOrSameLevelReason: lowerOrSameLevelReasonVisible
  } = visible;

  const documentBlacklist = useSelector((state: ApplicationState) => state.config.documentBlacklist);
  const sectionTitle = useSelector((state: ApplicationState) => state.config.Checklist.History.shortTitle);
  const isPageComplete = useSelector((state: ApplicationState) => state.student.offer.isHPage1Complete);
  const importData = useSelector((state: ApplicationState) => state.student.importData);

  return {
    sectionTitle,
    complete,
    currentStep: 1,
    steps: 3,
    lastCourse,
    lastCourseVisible,
    lastCourseConfig,
    lastCourseOther,
    lastCourseYear,
    lastCourseYearVisible,
    lastCourseYearConfig,
    hasOverseasTravel,
    hasOverseasTravelVisible,
    hasOverseasTravelConfig,
    overseasTravelTimes,
    overseasTravelTimesVisible,
    overseasTravelTimesConfig,
    hasUkTravel,
    hasUkTravelVisible,
    hasUkTravelConfig,
    ukTravelTimes,
    ukTravelTimesVisible,
    ukTravelTimesConfig,

    hasPreviousUkStudyVisa,
    hasPreviousUkStudyVisaConfig,
    hasPreviousUkStudy,
    hasPreviousUkStudyVisible,
    hasPreviousUkStudyConfig,

    lastCourseLevel,
    lastCourseLevelVisible,
    lastCourseLevelConfig,
    lowerOrSameLevelReason,
    lowerOrSameLevelReasonVisible,

    previousUkStudyVisas: previousUkStudyVisas?.length ? previousUkStudyVisas : [],
    documentBlacklist,
    config,
    isPageComplete,
    importData
  };
};

const HistoryQPage1 = () => {
  const {
    sectionTitle,
    complete,
    currentStep,
    steps,
    lastCourse,
    lastCourseVisible,
    lastCourseConfig,
    lastCourseOther,
    lastCourseYear,
    lastCourseYearVisible,
    lastCourseYearConfig,
    hasOverseasTravel,
    hasOverseasTravelVisible,
    hasOverseasTravelConfig,
    overseasTravelTimes,
    overseasTravelTimesVisible,
    overseasTravelTimesConfig,
    hasUkTravel,
    hasUkTravelVisible,
    hasUkTravelConfig,
    ukTravelTimes,
    ukTravelTimesVisible,
    ukTravelTimesConfig,
    hasPreviousUkStudyVisa,
    hasPreviousUkStudyVisaConfig,
    hasPreviousUkStudy,
    hasPreviousUkStudyVisible,
    hasPreviousUkStudyConfig,
    previousUkStudyVisas,
    lastCourseLevel,
    lastCourseLevelVisible,
    lastCourseLevelConfig,
    lowerOrSameLevelReason,
    lowerOrSameLevelReasonVisible,
    documentBlacklist,
    config,
    isPageComplete,
    importData
  } = useHistoryQPage1();

  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = config => {
    dispatch(StudentActions.saveHistory(Pages.PAGE1, config));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value, documentBlackList?: Array<string>) =>
    dispatch(HistoryActions.createUpdateHistoryFieldAction(fieldName, value, documentBlackList));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const prevUkStudyVisas = [
    ...(previousUkStudyVisas?.length ? previousUkStudyVisas : []),
    { visaType: '', level: '', year: '', isComplete: undefined }
  ];
  const [showModal, setShowModal] = useState([...previousUkStudyVisas.map(_ => false), false]);
  const [importApproved, setImportApproved] = useState([]);
  const [importUpdateEnabled, setImportUpdateEnabled] = useState([]);
  const [importEdited, setImportEdited] = useState([]);

  useEffect(() => {
    setImportApproved(getImportApprovedFields());
    setImportUpdateEnabled(getImportUpdateFields());
  }, [previousUkStudyVisas?.length, lastCourseYearVisible]);

  const getImportApprovedFields = () => {
    const importApproved = [];
    if (isPageComplete) {
      if (importData.prevUKStudy === hasPreviousUkStudy) {
        importApproved.push(Q.HAS_PREVIOUS_UK_STUDY);
      }
    }
    return importApproved;
  };

  const getImportUpdateFields = () => {
    const importUpdateEnabled = [];
    if (isPageComplete) {
      if (importData.prevUKStudy !== undefined && hasPreviousUkStudy !== importData.prevUKStudy) {
        importUpdateEnabled.push(Q.HAS_PREVIOUS_UK_STUDY);
      }
    }
    return importUpdateEnabled;
  };

  const isImportEnabled = target => {
    switch (target) {
      case Q.HAS_PREVIOUS_UK_STUDY:
        return hasPreviousUkStudyVisible && importData.prevUKStudy !== undefined;
      default:
        return false;
    }
  };

  const isImportUpdateEnabled = target => {
    if (!isPageComplete) return false;
    switch (target) {
      case Q.HAS_UK_TRAVEL:
        return hasUkTravel !== undefined && importUpdateEnabled.includes(target);
      case Q.HAS_PREVIOUS_UK_STUDY:
        return hasPreviousUkStudy !== undefined && importUpdateEnabled.includes(target);
      default:
        return false;
    }
  };

  const isImportComplete = target => {
    if (!isImportEnabled(target) || isImportUpdateEnabled(target)) return true;
    return importApproved.includes(target) || importEdited.includes(target);
  };

  const handleImportApprove = target => {
    setImportApproved([...importApproved, target]);
    setImportEdited(importEdited.filter(q => q !== target));
  };

  const handleImportEdit = target => {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited([...importEdited, target]);
  };

  const handleImportUndo = target => {
    setImportApproved(importApproved.filter(q => q !== target));
    setImportEdited(importEdited.filter(q => q !== target));
  };

  const handleYesNoClick = (target, question) => {
    onChange(target, question.char === 'Y' ? true : false);
  };

  const handleOTTClick = question => {
    onChange(
      Q.OVERSEAS_TRAVEL_TIMES,
      question.char === '1' ? 'Once' : question.char === '2' ? 'Less than 5' : '5 or more'
    );
  };

  const handleUKTTClick = question => {
    onChange(Q.UK_TRAVEL_TIMES, question.char === '1' ? 'Once' : question.char === '2' ? 'Less than 5' : '5 or more');
  };

  const handleLastCourseLevelClick = question => {
    onChange(
      Q.LAST_COURSE_LEVEL,
      question.char === '1'
        ? types.CourseLevel.HIGHER
        : question.char === '2'
        ? types.CourseLevel.SAME
        : question.char === '3'
        ? types.CourseLevel.LOWER
        : types.CourseLevel.NO_UK_STUDY
    );
  };

  const toggleModal = index => {
    setShowModal(prevState => {
      const newShowModal = [...prevState];
      newShowModal[index] = !prevState[index];
      return newShowModal;
    });
  };

  const deleteVisa = index => {
    const visas = [...prevUkStudyVisas];
    onChange(Q.PREVIOUS_UK_VISAS, [...visas.slice(0, index), ...visas.slice(index + 1)]);
  };

  const getVisaItems = visa => {
    let cardItems = [
      {
        label: 'Study Level',
        value: visa.level
      },
      {
        label: 'Year',
        value: visa.year
      },
      {
        label: 'Completed',
        value: visa.isComplete ? 'Yes' : 'No'
      }
    ];
    if (!visa.isComplete) {
      cardItems = [
        ...cardItems,
        {
          label: 'Incomplete Reason',
          value: visa.incompleteReason ? visa.incompleteReason : ''
        },
        {
          label: 'Incomplete Record',
          value: visa.isIncompleteRecorded ? 'Yes' : 'No'
        },
        {
          label: 'Is Visa Shortened',
          value: visa.isVisaShortened ? 'Yes' : 'No'
        }
      ];
      if (visa.isVisaShortened) {
        cardItems = [
          ...cardItems,
          {
            label: 'Visa Shortened To',
            value: `${getFormatDateFromStoreDate(visa.shortenedDate)}`
          }
        ];
      }
    }
    if (visa) {
      return cardItems;
    }
    return cardItems;
  };

  const getComplete = () => {
    const importQuestions = [Q.SURNAME, Q.GIVEN_NAME];
    const importComplete = importQuestions.every(q => isImportComplete(q));
    return complete && importComplete;
  };

  return (
    <QuestionPage
      complete={getComplete()}
      currentStep={currentStep}
      steps={steps}
      onNext={() => onNext(config)}
      onSkip={onSkip}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      {lastCourseVisible && (
        <SelectWithOtherWithConfirm
          key={Q.LAST_COURSE}
          id={Q.LAST_COURSE}
          label="What was the last course you successfully completed?"
          autoFocus={true}
          options={[
            { label: 'High School', value: 'High School' },
            { label: 'A Level', value: 'A Level' },
            {
              label: 'International Baccalaureate diploma',
              value: 'Baccalaureate'
            },
            { label: 'University Foundation', value: 'Foundation' },
            { label: 'Certificate of Higher Education', value: 'CoHE' },
            {
              label: 'Diploma of Higher Education (DipHE)',
              value: 'DipHe'
            },
            { label: 'Higher National Diploma (HND)', value: 'HND' },
            { label: 'Bachelor Degree (UG)', value: 'UG' },
            { label: `Master's Degree (PG)`, value: 'PG' },
            { label: 'Graduate Certificate or Diploma', value: 'GC' },
            {
              label: 'Post Graduate Certificate or Diploma',
              value: 'PGC'
            },
            { label: 'Research Degree', value: 'Research' }
          ]}
          otherOptions={[
            {
              label: 'Other',
              value: 'Other'
            }
          ]}
          otherOptionLabel="Please state why you chose other:"
          value={lastCourse}
          onChange={evt => onChange(Q.LAST_COURSE, evt.currentTarget.value, documentBlacklist)}
          onChangeOtherText={evt => onChange(Q.LAST_COURSE_OTHER, evt.currentTarget.value)}
          inputHelperText={lastCourseConfig?.helpText}
          otherValue={lastCourseOther}
        />
      )}

      {lastCourseYearVisible && (
        <YearInputWithConfirm
          key={Q.LAST_COURSE_YEAR}
          id={Q.LAST_COURSE_YEAR}
          value={lastCourseYear}
          label={'In which year did you complete this course?'}
          inputHelperText={lastCourseYearConfig?.helpText}
          onChange={evt => onChange(Q.LAST_COURSE_YEAR, evt.currentTarget.value)}
          maxLength={4}
          placeholder="YYYY"
        />
      )}

      {hasOverseasTravelVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_OVERSEAS_TRAVEL}
          id={Q.HAS_OVERSEAS_TRAVEL}
          label={'Have you ever travelled outside of your home country in the last 10 years?'}
          inputHelperText={hasOverseasTravelConfig?.helpText}
          onClick={question => handleYesNoClick(Q.HAS_OVERSEAS_TRAVEL, question)}
          isInline={true}
          maxQuestions={2}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasOverseasTravel === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasOverseasTravel === false
            }
          ]}
        />
      )}

      {overseasTravelTimesVisible && (
        <RadioOptionsWithConfirm
          key={Q.OVERSEAS_TRAVEL_TIMES}
          id={Q.OVERSEAS_TRAVEL_TIMES}
          value={overseasTravelTimes}
          label={'Approximately how many times?'}
          inputHelperText={overseasTravelTimesConfig?.helpText}
          onClick={question => handleOTTClick(question)}
          questions={[
            {
              char: '1',
              label: 'Once',
              selected: overseasTravelTimes === 'Once'
            },
            {
              char: '2',
              label: 'Less than 5',
              selected: overseasTravelTimes === 'Less than 5'
            },
            {
              char: '3',
              label: '5 or more',
              selected: overseasTravelTimes === '5 or more'
            }
          ]}
        />
      )}

      {hasUkTravelVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_UK_TRAVEL}
          id={Q.HAS_UK_TRAVEL}
          value={hasUkTravel}
          label={'Have you ever travelled to the UK in the last 10 years?'}
          inputHelperText={hasUkTravelConfig?.helpText}
          onClick={question => handleYesNoClick(Q.HAS_UK_TRAVEL, question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasUkTravel === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasUkTravel === false
            }
          ]}
        />
      )}

      {ukTravelTimesVisible && (
        <RadioOptionsWithConfirm
          key={Q.UK_TRAVEL_TIMES}
          id={Q.UK_TRAVEL_TIMES}
          value={ukTravelTimes}
          label={'Approximately how many times?'}
          inputHelperText={ukTravelTimesConfig?.helpText}
          onClick={question => handleUKTTClick(question)}
          questions={[
            {
              char: '1',
              label: 'Once',
              selected: ukTravelTimes === 'Once'
            },
            {
              char: '2',
              label: 'Less than 5',
              selected: ukTravelTimes === 'Less than 5'
            },
            {
              char: '3',
              label: '5 or more',
              selected: ukTravelTimes === '5 or more'
            }
          ]}
        />
      )}

      {hasPreviousUkStudyVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_PREVIOUS_UK_STUDY}
          id={Q.HAS_PREVIOUS_UK_STUDY}
          label={'Do you have any previous UK study?'}
          inputHelperText={hasPreviousUkStudyConfig?.helpText}
          onClick={question => handleYesNoClick(Q.HAS_PREVIOUS_UK_STUDY, question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasPreviousUkStudy === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasPreviousUkStudy === false
            }
          ]}
          importEnabled={isImportEnabled(Q.HAS_PREVIOUS_UK_STUDY)}
          importValue={importData.prevUKStudy ? 'Yes' : 'No'}
          importUpdateEnabled={isImportUpdateEnabled(Q.HAS_PREVIOUS_UK_STUDY)}
          importApproved={importApproved.includes(Q.HAS_PREVIOUS_UK_STUDY)}
          importEdited={importEdited.includes(Q.HAS_PREVIOUS_UK_STUDY)}
          onImportApprove={() => {
            handleImportApprove(Q.HAS_PREVIOUS_UK_STUDY);
            onChange(Q.HAS_PREVIOUS_UK_STUDY, importData.prevUKStudy);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.HAS_PREVIOUS_UK_STUDY);
            onChange(
              Q.HAS_PREVIOUS_UK_STUDY,
              isImportUpdateEnabled(Q.HAS_PREVIOUS_UK_STUDY) ? hasPreviousUkStudy : importData.prevUKStudy
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.HAS_PREVIOUS_UK_STUDY);
            onChange(Q.HAS_PREVIOUS_UK_STUDY, isImportUpdateEnabled(Q.HAS_PREVIOUS_UK_STUDY) ? hasPreviousUkStudy : '');
          }}
        />
      )}

      {hasPreviousUkStudy && (
        <>
          <RadioOptionsWithConfirm
            key={Q.HAS_PREVIOUS_UK_STUDY_VISA}
            id={Q.HAS_PREVIOUS_UK_STUDY_VISA}
            label={'Did you receive a student visa for your previous UK study?'}
            inputHelperText={hasPreviousUkStudyVisaConfig?.helpText}
            onClick={question => handleYesNoClick(Q.HAS_PREVIOUS_UK_STUDY_VISA, question)}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasPreviousUkStudyVisa === true
              },
              {
                char: 'N',
                label: 'No',
                selected: hasPreviousUkStudyVisa === false
              }
            ]}
          />

          {hasPreviousUkStudyVisa && (
            <>
              <CardContainer title={`UK Student Visas`}>
                {prevUkStudyVisas.map((visa: IStudentVisa, index: number) => (
                  <CardWithModal
                    key={index}
                    id={`visa-${index}`}
                    index={index}
                    modalTitle={'Add a UK Student Visa'}
                    cardTitle={visa.visaType}
                    cardItems={getVisaItems(visa)}
                    onRemove={deleteVisa}
                    addButtonLabel={`Add Previous UK VISA`}
                    addAnotherButtonLabel={`I have another visa to add`}
                    hasAddMoreButton={
                      visa.visaType === '' && visa.level === '' && visa.year === '' && visa.isComplete === undefined
                    }
                    showModal={showModal[index]}
                    toggleModal={toggleModal}
                  >
                    <Visas
                      index={index}
                      visaType={visa.visaType}
                      level={visa.level}
                      year={visa.year}
                      isComplete={visa.isComplete}
                      incompleteReason={visa.incompleteReason}
                      isIncompleteRecorded={visa.isIncompleteRecorded}
                      isVisaShortened={visa.isVisaShortened}
                      shortenedDate={visa.shortenedDate}
                      onSaveAndClose={(visa: IStudentVisa) => {
                        const prevUkStudyVisas = [...previousUkStudyVisas];
                        prevUkStudyVisas[index] = {
                          ...visa
                        };
                        onChange(Q.PREVIOUS_UK_VISAS, prevUkStudyVisas);
                        toggleModal(index);
                      }}
                    />
                  </CardWithModal>
                ))}
              </CardContainer>

              {lastCourseLevelVisible && (
                <>
                  <RadioOptionsWithConfirm
                    key={Q.LAST_COURSE_LEVEL}
                    id={Q.LAST_COURSE_LEVEL}
                    value={lastCourseLevel}
                    label={
                      'Are you applying for a course that is at a higher, ' +
                      'same or lower-level than the last course you studied in the UK?'
                    }
                    inputHelperText={lastCourseLevelConfig?.helpText}
                    onClick={question => handleLastCourseLevelClick(question)}
                    questions={[
                      {
                        char: '1',
                        label: 'The course I am applying for now is at a higher level',
                        selected: lastCourseLevel === types.CourseLevel.HIGHER
                      },
                      {
                        char: '2',
                        label: 'The course I am applying for now is at the same level',
                        selected: lastCourseLevel === types.CourseLevel.SAME
                      },
                      {
                        char: '3',
                        label: 'The course I am applying for now is at a lower level',
                        selected: lastCourseLevel === types.CourseLevel.LOWER
                      },
                      {
                        char: '4',
                        label: 'I have not studied any academic courses in the UK',
                        selected: lastCourseLevel === types.CourseLevel.NO_UK_STUDY
                      }
                    ]}
                  />
                </>
              )}

              {lowerOrSameLevelReasonVisible && (
                <TextareaWithConfirm
                  id={Q.REASON_WHY_LOWER_OR_SAME_LEVEL}
                  value={lowerOrSameLevelReason}
                  label="Why are you applying for a course at the same or lower level as your previous course?"
                  subText="Please explain how will this course support your genuine career aspirations."
                  onChange={evt => onChange(Q.REASON_WHY_LOWER_OR_SAME_LEVEL, evt.currentTarget.value)}
                />
              )}
            </>
          )}
        </>
      )}
    </QuestionPage>
  );
};

export default HistoryQPage1;
